/**
 * Shrink a photo in the browser before upload.
 *
 * A phone photo is 3-8 MB and 4000+ px wide; documenting a ticket needs
 * neither. Re-encoding to a JPEG no larger than `maxDim` on its long edge
 * keeps uploads to a few hundred KB, so they work over a rig's cellular link
 * and stay well under the API's 5 MB cap. No dependencies: an <img> decodes
 * the file (browsers apply the EXIF orientation when they do, so the photo
 * isn't rotated) and a <canvas> re-encodes it.
 */

const DEFAULT_MAX_DIM = 1600;
const DEFAULT_QUALITY = 0.85;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file could not be read as an image."));
    };
    img.src = url;
  });
}

/** Scale (w, h) down so the long edge is at most `maxDim`. Never scales up. */
export function fitWithin(w: number, h: number, maxDim: number): [number, number] {
  const longEdge = Math.max(w, h);
  if (longEdge <= maxDim || longEdge === 0) return [w, h];
  const scale = maxDim / longEdge;
  return [Math.round(w * scale), Math.round(h * scale)];
}

/** Resize + re-encode `file` as a JPEG data URL. */
export async function resizeImageToDataUrl(
  file: File,
  maxDim: number = DEFAULT_MAX_DIM,
  quality: number = DEFAULT_QUALITY,
): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  const img = await loadImage(file);
  const [w, h] = fitWithin(img.naturalWidth, img.naturalHeight, maxDim);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser cannot process images.");
  // JPEG has no transparency: paint white first so a PNG with an alpha
  // channel doesn't come out with a black background.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}
